import React, { useState } from 'react';
import { XIcon, FileTextIcon, CameraIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './Button';
import { Input } from './Input';
import { Select } from './Select';
export interface ReportData {
  id?: string;
  title: string;
  reportType: 'Standard Installation' | 'Nedbrud/Udskiftning' | 'Flytning/Nedtagning' | 'Projektering' | 'Forgæves kørsel';
  date: string;
  customerId?: string;
  taskId?: string;
  category: 'all' | 'Ladeboks' | 'Andet';
  notes: string;
  createdAt: string;
  // Detailed inspection fields
  meterNumber?: string;
  installationNumber?: string;
  cleanedUp?: 'yes' | 'no';
  ownerInformed?: 'yes' | 'no' | 'not-relevant';
  earthConnectionAccessibility?: 'yes' | 'no';
  barriersOrEnclosures?: 'yes' | 'no' | 'not-relevant';
  sufficientWorkingSpace?: 'yes' | 'no';
  cablesCorrectlyLaid?: 'yes' | 'no' | 'not-relevant';
  cablesCorrectlyTerminated?: 'yes' | 'no' | 'not-relevant';
  mechanicalProtection?: 'yes' | 'no' | 'not-relevant';
  circuitsLabeledWithAffiliation?: 'yes' | 'no' | 'not-relevant';
  circuitsLabeledWithMaxFuse?: 'yes' | 'no' | 'not-relevant';
  materialsInstalledPerInstructions?: 'yes' | 'no';
  protectionConductorContinuity?: string;
  insulationMeasurement?: string;
  voltageControl?: string;
  rcdTripTime?: string;
  earthElectrodeResistance?: string;
  // Photos
  photoMeterCabinet?: string;
  photoChargingBox3m?: string;
  photoPanelWithoutCover?: string;
  photoPanelWithCover?: string;
  photoCableRouting?: string;
}
interface AddReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (report: Omit<ReportData, 'id' | 'createdAt'>) => void;
  customers: Array<{
    id: string;
    name: string;
    customerId: string;
  }>;
  tasks: Array<{
    id: string;
    taskId: string;
    title: string;
    customerId: string;
  }>;
  editingReport?: ReportData | null;
}
export function AddReportModal({
  isOpen,
  onClose,
  onAdd,
  customers,
  tasks,
  editingReport
}: AddReportModalProps) {
  const [title, setTitle] = useState(editingReport?.title || '');
  const [reportType, setReportType] = useState<ReportData['reportType']>(editingReport?.reportType || 'Standard Installation');
  const [date, setDate] = useState(editingReport?.date || '');
  const [customerId, setCustomerId] = useState(editingReport?.customerId || '');
  const [taskId, setTaskId] = useState(editingReport?.taskId || '');
  const [category, setCategory] = useState<'all' | 'Ladeboks' | 'Andet'>(editingReport?.category || 'all');
  const [notes, setNotes] = useState(editingReport?.notes || '');
  // Detailed inspection fields
  const [meterNumber, setMeterNumber] = useState(editingReport?.meterNumber || '');
  const [installationNumber, setInstallationNumber] = useState(editingReport?.installationNumber || '');
  const [cleanedUp, setCleanedUp] = useState<'yes' | 'no'>(editingReport?.cleanedUp || 'yes');
  const [ownerInformed, setOwnerInformed] = useState<'yes' | 'no' | 'not-relevant'>(editingReport?.ownerInformed || 'yes');
  const [earthConnectionAccessibility, setEarthConnectionAccessibility] = useState<'yes' | 'no'>(editingReport?.earthConnectionAccessibility || 'yes');
  const [barriersOrEnclosures, setBarriersOrEnclosures] = useState<'yes' | 'no' | 'not-relevant'>(editingReport?.barriersOrEnclosures || 'yes');
  const [sufficientWorkingSpace, setSufficientWorkingSpace] = useState<'yes' | 'no'>(editingReport?.sufficientWorkingSpace || 'yes');
  const [cablesCorrectlyLaid, setCablesCorrectlyLaid] = useState<'yes' | 'no' | 'not-relevant'>(editingReport?.cablesCorrectlyLaid || 'yes');
  const [cablesCorrectlyTerminated, setCablesCorrectlyTerminated] = useState<'yes' | 'no' | 'not-relevant'>(editingReport?.cablesCorrectlyTerminated || 'yes');
  const [mechanicalProtection, setMechanicalProtection] = useState<'yes' | 'no' | 'not-relevant'>(editingReport?.mechanicalProtection || 'yes');
  const [circuitsLabeledWithAffiliation, setCircuitsLabeledWithAffiliation] = useState<'yes' | 'no' | 'not-relevant'>(editingReport?.circuitsLabeledWithAffiliation || 'yes');
  const [circuitsLabeledWithMaxFuse, setCircuitsLabeledWithMaxFuse] = useState<'yes' | 'no' | 'not-relevant'>(editingReport?.circuitsLabeledWithMaxFuse || 'yes');
  const [materialsInstalledPerInstructions, setMaterialsInstalledPerInstructions] = useState<'yes' | 'no'>(editingReport?.materialsInstalledPerInstructions || 'yes');
  const [protectionConductorContinuity, setProtectionConductorContinuity] = useState(editingReport?.protectionConductorContinuity || '');
  const [insulationMeasurement, setInsulationMeasurement] = useState(editingReport?.insulationMeasurement || '');
  const [voltageControl, setVoltageControl] = useState(editingReport?.voltageControl || '');
  const [rcdTripTime, setRcdTripTime] = useState(editingReport?.rcdTripTime || '');
  const [earthElectrodeResistance, setEarthElectrodeResistance] = useState(editingReport?.earthElectrodeResistance || '');
  // Photo states
  const [photoMeterCabinet, setPhotoMeterCabinet] = useState(editingReport?.photoMeterCabinet || '');
  const [photoChargingBox3m, setPhotoChargingBox3m] = useState(editingReport?.photoChargingBox3m || '');
  const [photoPanelWithoutCover, setPhotoPanelWithoutCover] = useState(editingReport?.photoPanelWithoutCover || '');
  const [photoPanelWithCover, setPhotoPanelWithCover] = useState(editingReport?.photoPanelWithCover || '');
  const [photoCableRouting, setPhotoCableRouting] = useState(editingReport?.photoCableRouting || '');
  // Filter tasks by selected customer
  const filteredTasks = customerId ? tasks.filter(task => task.customerId === customerId) : [];
  const handlePhotoUpload = (setter: (value: string) => void) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setter(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !date) return;
    onAdd({
      title,
      reportType,
      date,
      customerId: customerId || undefined,
      taskId: taskId || undefined,
      category,
      notes,
      // Detailed fields
      meterNumber: meterNumber || undefined,
      installationNumber: installationNumber || undefined,
      cleanedUp,
      ownerInformed,
      earthConnectionAccessibility,
      barriersOrEnclosures,
      sufficientWorkingSpace,
      cablesCorrectlyLaid,
      cablesCorrectlyTerminated,
      mechanicalProtection,
      circuitsLabeledWithAffiliation,
      circuitsLabeledWithMaxFuse,
      materialsInstalledPerInstructions,
      protectionConductorContinuity: protectionConductorContinuity || undefined,
      insulationMeasurement: insulationMeasurement || undefined,
      voltageControl: voltageControl || undefined,
      rcdTripTime: rcdTripTime || undefined,
      earthElectrodeResistance: earthElectrodeResistance || undefined,
      // Photos
      photoMeterCabinet: photoMeterCabinet || undefined,
      photoChargingBox3m: photoChargingBox3m || undefined,
      photoPanelWithoutCover: photoPanelWithoutCover || undefined,
      photoPanelWithCover: photoPanelWithCover || undefined,
      photoCableRouting: photoCableRouting || undefined
    });
    onClose();
  };
  const RadioGroup = ({
    label,
    value,
    onChange,
    options
  }: {
    label: string;
    value: string;
    onChange: (value: any) => void;
    options: Array<{
      value: string;
      label: string;
    }>;
  }) => <div data-id="element-113" data-mp="39f95kijnsvr6">
      <label className="block text-sm font-medium text-stone-700 mb-2" data-id="element-114" data-mp="39in93u427b07">
        {label}
      </label>
      <div className="flex gap-4" data-id="element-115" data-mp="3dft0rgko9pao">
        {options.map(option => <label key={option.value} className="flex items-center gap-2 cursor-pointer" data-id="element-116" data-mp="91w36inbvz0b">
            <input type="radio" value={option.value} checked={value === option.value} onChange={e => onChange(e.target.value)} className="w-4 h-4 text-teal-600 focus:ring-teal-500" data-id="element-117" data-mp="43ceiahco949" />
            <span className="text-sm text-stone-700" data-id="element-118" data-mp="rpj0k0nlzpdh">{option.label}</span>
          </label>)}
      </div>
    </div>;
  return <AnimatePresence data-id="element-119" data-mp="nrl9ste6wv7z">
      {isOpen && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40" onClick={onClose} data-id="element-120" data-mp="ix7k97m7lib8" />
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        type: 'spring',
        duration: 0.3
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-121" data-mp="16ms21n6mm9ix">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto p-6" data-id="element-122" data-mp="21wn9qfzc4e4s">
              <div className="flex items-center justify-between mb-6 sticky top-0 bg-white pb-4 border-b border-stone-200" data-id="element-123" data-mp="26nn7fnga308c">
                <div className="flex items-center gap-3" data-id="element-124" data-mp="2bmgmhkb7qzam">
                  <div className="p-2 bg-teal-100 rounded-lg" data-id="element-125" data-mp="2fp3i7wb094d3">
                    <FileTextIcon className="w-6 h-6 text-teal-700" data-id="element-126" data-mp="2fmigd7apiofc" />
                  </div>
                  <div data-id="element-127" data-mp="2kllkmpiv4qm9">
                    <h2 className="text-2xl font-semibold text-stone-900" data-id="element-128" data-mp="2rxzexkp2u5hs">
                      {editingReport ? 'Rediger Rapport' : 'Tilføj Rapport'}
                    </h2>
                    <p className="text-sm text-stone-500 mt-1" data-id="element-129" data-mp="2wuycyebv2wl9">
                      Udfyld alle relevante felter for rapporten
                    </p>
                  </div>
                </div>
                <button onClick={onClose} className="text-stone-400 hover:text-stone-600 transition-colors" aria-label="Luk" data-id="element-130" data-mp="36oyp305k923h">
                  <XIcon className="w-6 h-6" data-id="element-131" data-mp="3fpn2ptr1l0fu" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-8" data-id="element-132" data-mp="3kner4dsha4fa">
                {/* Basic Information */}
                <div className="bg-stone-50 rounded-xl p-6 border border-stone-200" data-id="element-133" data-mp="3pjncwa16kvvf">
                  <h3 className="text-lg font-semibold text-stone-900 mb-4 flex items-center gap-2" data-id="element-134" data-mp="1qagli7hq3vya">
                    <span className="w-8 h-8 bg-teal-600 text-white rounded-full flex items-center justify-center text-sm font-bold" data-id="element-135" data-mp="1v7yw02be2j8e">
                      1
                    </span>
                    Grundlæggende information
                  </h3>

                  <div className="space-y-4" data-id="element-136" data-mp="3jhdkkcbofjv">
                    <Input label="Rapport titel" value={title} onChange={e => setTitle(e.target.value)} placeholder="f.eks. Installation rapport - Januar 2024" required data-id="element-137" data-mp="3kisizwglm6v" />

                    <div className="grid grid-cols-2 gap-4" data-id="element-138" data-mp="r7qsd2b4c31i">
                      <Select label="Opgavetype" value={reportType} onChange={e => setReportType(e.target.value as ReportData['reportType'])} options={[{
                    value: 'Standard Installation',
                    label: 'Standard Installation'
                  }, {
                    value: 'Nedbrud/Udskiftning',
                    label: 'Nedbrud/Udskiftning'
                  }, {
                    value: 'Flytning/Nedtagning',
                    label: 'Flytning/Nedtagning'
                  }, {
                    value: 'Projektering',
                    label: 'Projektering'
                  }, {
                    value: 'Forgæves kørsel',
                    label: 'Forgæves kørsel'
                  }]} required data-id="element-139" data-mp="m9pdktkq6pvu" />

                      <Select label="Kunde" value={customerId} onChange={e => {
                    setCustomerId(e.target.value);
                    setTaskId(''); // Reset task when customer changes
                  }} options={[{
                    value: '',
                    label: 'Vælg kunde'
                  }, ...customers.map(c => ({
                    value: c.id,
                    label: `${c.name} (${c.customerId})`
                  }))]} data-id="element-140" data-mp="26t2n8szmsgcx" />
                    </div>

                    {/* NEW: Task selection - only shown if customer is selected */}
                    {customerId && filteredTasks.length > 0 && <Select label="Tilknyt opgave (valgfrit)" value={taskId} onChange={e => setTaskId(e.target.value)} options={[{
                  value: '',
                  label: 'Ingen opgave'
                }, ...filteredTasks.map(task => ({
                  value: task.id,
                  label: `${task.taskId} - ${task.title}`
                }))]} data-id="element-141" data-mp="2ox63tosbbv8b" />}

                    <div className="grid grid-cols-2 gap-4" data-id="element-142" data-mp="48bdx3pnwlkj">
                      <Input label="Målernummer" value={meterNumber} onChange={e => setMeterNumber(e.target.value)} placeholder="Indtast målernummer" data-id="element-143" data-mp="48l8z5pkdix3" />

                      <Input label="Installationsnummer" value={installationNumber} onChange={e => setInstallationNumber(e.target.value)} placeholder="Indtast installationsnummer" data-id="element-144" data-mp="1kljjf76ncjj3" />
                    </div>

                    <Input label="Dato" type="date" value={date} onChange={e => setDate(e.target.value)} required data-id="element-145" data-mp="355f5pdsiv0u9" />
                  </div>
                </div>

                {/* General Checks */}
                <div className="bg-stone-50 rounded-xl p-6 border border-stone-200" data-id="element-146" data-mp="2lgmluvbj0s3j">
                  <h3 className="text-lg font-semibold text-stone-900 mb-4 flex items-center gap-2" data-id="element-147" data-mp="2lizatsfpc2q3">
                    <span className="w-8 h-8 bg-teal-600 text-white rounded-full flex items-center justify-center text-sm font-bold" data-id="element-148" data-mp="2hgvx0x5k8m1n">
                      2
                    </span>
                    Generelle kontroller
                  </h3>

                  <div className="space-y-4" data-id="element-149" data-mp="apms0jy0n6vb">
                    <RadioGroup label="Er der rengjort og ryddet op efter arbejdet?" value={cleanedUp} onChange={setCleanedUp} options={[{
                  value: 'yes',
                  label: 'Ja'
                }, {
                  value: 'no',
                  label: 'Nej'
                }]} data-id="element-150" data-mp="amqtoui9ag6c" />

                    <RadioGroup label="Er ejer/bruger informeret om funktion og betjening?" value={ownerInformed} onChange={setOwnerInformed} options={[{
                  value: 'yes',
                  label: 'Ja'
                }, {
                  value: 'no',
                  label: 'Nej'
                }, {
                  value: 'not-relevant',
                  label: 'Ikke relevant'
                }]} data-id="element-151" data-mp="1hj2gitfzrrru" />

                    <RadioGroup label="Tilgængelighed af jordlederforbindelser" value={earthConnectionAccessibility} onChange={setEarthConnectionAccessibility} options={[{
                  value: 'yes',
                  label: 'Ja'
                }, {
                  value: 'no',
                  label: 'Nej'
                }]} data-id="element-152" data-mp="3uoft4zrb32cx" />

                    <RadioGroup label="Barrierer eller kapslinger" value={barriersOrEnclosures} onChange={setBarriersOrEnclosures} options={[{
                  value: 'yes',
                  label: 'Ja'
                }, {
                  value: 'no',
                  label: 'Nej'
                }, {
                  value: 'not-relevant',
                  label: 'Ikke relevant'
                }]} data-id="element-153" data-mp="2ja28e3run314" />

                    <RadioGroup label="Tilstrækkelig plads til at arbejde/adgang til materiel (tavle)" value={sufficientWorkingSpace} onChange={setSufficientWorkingSpace} options={[{
                  value: 'yes',
                  label: 'Ja'
                }, {
                  value: 'no',
                  label: 'Nej'
                }]} data-id="element-154" data-mp="2gpv5xt3of39a" />
                  </div>
                </div>

                {/* Cable Installation */}
                <div className="bg-stone-50 rounded-xl p-6 border border-stone-200" data-id="element-155" data-mp="jq4d4i8210do">
                  <h3 className="text-lg font-semibold text-stone-900 mb-4 flex items-center gap-2" data-id="element-156" data-mp="esupn8486ysh">
                    <span className="w-8 h-8 bg-teal-600 text-white rounded-full flex items-center justify-center text-sm font-bold" data-id="element-157" data-mp="9usxlxotnp4v">
                      3
                    </span>
                    Kabelinstallation
                  </h3>

                  <div className="space-y-4" data-id="element-158" data-mp="xqxbje1h8fm2">
                    <RadioGroup label="Kabler korrekt oplagt" value={cablesCorrectlyLaid} onChange={setCablesCorrectlyLaid} options={[{
                  value: 'yes',
                  label: 'Ja'
                }, {
                  value: 'no',
                  label: 'Nej'
                }, {
                  value: 'not-relevant',
                  label: 'Ikke relevant'
                }]} data-id="element-159" data-mp="xpm5t8oh0xsb" />

                    <RadioGroup label="Kabler korrekt afsluttet i kapslinger" value={cablesCorrectlyTerminated} onChange={setCablesCorrectlyTerminated} options={[{
                  value: 'yes',
                  label: 'Ja'
                }, {
                  value: 'no',
                  label: 'Nej'
                }, {
                  value: 'not-relevant',
                  label: 'Ikke relevant'
                }]} data-id="element-160" data-mp="2y35mz3ns5o0t" />

                    <RadioGroup label="Mekanisk beskyttelse af strømkredse/kabler (f.eks. hvor de kommer op fra jord, er skjult over loft, under gulv eller i vægge)" value={mechanicalProtection} onChange={setMechanicalProtection} options={[{
                  value: 'yes',
                  label: 'Ja'
                }, {
                  value: 'no',
                  label: 'Nej'
                }, {
                  value: 'not-relevant',
                  label: 'Ikke relevant'
                }]} data-id="element-161" data-mp="kwp7dr1vckjg" />
                  </div>
                </div>

                {/* Labeling and Materials */}
                <div className="bg-stone-50 rounded-xl p-6 border border-stone-200" data-id="element-162" data-mp="1rkcmd8ozmf01">
                  <h3 className="text-lg font-semibold text-stone-900 mb-4 flex items-center gap-2" data-id="element-163" data-mp="3nlxs2i7o9s0w">
                    <span className="w-8 h-8 bg-teal-600 text-white rounded-full flex items-center justify-center text-sm font-bold" data-id="element-164" data-mp="3jm5ji5ia3lvj">
                      4
                    </span>
                    Mærkning og materialer
                  </h3>

                  <div className="space-y-4" data-id="element-165" data-mp="3sjzdvawhg5g2">
                    <RadioGroup label="Installationer og strømkredse mærket med tilhørsforhold" value={circuitsLabeledWithAffiliation} onChange={setCircuitsLabeledWithAffiliation} options={[{
                  value: 'yes',
                  label: 'Ja'
                }, {
                  value: 'no',
                  label: 'Nej'
                }, {
                  value: 'not-relevant',
                  label: 'Ikke relevant'
                }]} data-id="element-166" data-mp="b49e59yj6nbc" />

                    <RadioGroup label="Installationer og strømkredse mærket med max sikringsstørrelse" value={circuitsLabeledWithMaxFuse} onChange={setCircuitsLabeledWithMaxFuse} options={[{
                  value: 'yes',
                  label: 'Ja'
                }, {
                  value: 'no',
                  label: 'Nej'
                }, {
                  value: 'not-relevant',
                  label: 'Ikke relevant'
                }]} data-id="element-167" data-mp="11hfhb6opkq46" />

                    <RadioGroup label="Valgte materialer installeret efter anvisninger" value={materialsInstalledPerInstructions} onChange={setMaterialsInstalledPerInstructions} options={[{
                  value: 'yes',
                  label: 'Ja'
                }, {
                  value: 'no',
                  label: 'Nej'
                }]} data-id="element-168" data-mp="2356qqknhel33" />
                  </div>
                </div>

                {/* Measurements */}
                <div className="bg-stone-50 rounded-xl p-6 border border-stone-200" data-id="element-169" data-mp="qmlr5u1fg2lg">
                  <h3 className="text-lg font-semibold text-stone-900 mb-4 flex items-center gap-2" data-id="element-170" data-mp="ln953bidqzjx">
                    <span className="w-8 h-8 bg-teal-600 text-white rounded-full flex items-center justify-center text-sm font-bold" data-id="element-171" data-mp="go5znpyhmrbf">
                      5
                    </span>
                    Målinger
                  </h3>

                  <div className="space-y-4" data-id="element-172" data-mp="2jdgzhnm0rwb3">
                    <Input label="Kontinuitet i beskyttelsesleder og/eller udligningsforbindelse" value={protectionConductorContinuity} onChange={e => setProtectionConductorContinuity(e.target.value)} placeholder="Indtast måleværdi" data-id="element-173" data-mp="2jco1bb14mf4k" />

                    <Input label="Isolationsmåling af installationen (MΩ)" value={insulationMeasurement} onChange={e => setInsulationMeasurement(e.target.value)} placeholder="f.eks. 500 MΩ" data-id="element-174" data-mp="1zqj7z4imkhyh" />

                    <Input label="Kontrol af spændingen (V)" value={voltageControl} onChange={e => setVoltageControl(e.target.value)} placeholder="f.eks. 230V" data-id="element-175" data-mp="2ef5o38g2tdqr" />

                    <Input label="Kontrol af fejlstrømsafbryder, udkoblingstid i ms" value={rcdTripTime} onChange={e => setRcdTripTime(e.target.value)} placeholder="f.eks. 25ms" data-id="element-176" data-mp="1dvsre7utuf5e" />

                    <Input label="Overgangsmodstand til jordelektrode (Ω)" value={earthElectrodeResistance} onChange={e => setEarthElectrodeResistance(e.target.value)} placeholder="f.eks. 10Ω" data-id="element-177" data-mp="1inatiz8l6lry" />
                  </div>
                </div>

                {/* Photo Documentation */}
                <div className="bg-stone-50 rounded-xl p-6 border border-stone-200" data-id="element-178" data-mp="zz5sw0n87gxd">
                  <h3 className="text-lg font-semibold text-stone-900 mb-4 flex items-center gap-2" data-id="element-179" data-mp="2g9rpa01o8rbl">
                    <span className="w-8 h-8 bg-teal-600 text-white rounded-full flex items-center justify-center text-sm font-bold" data-id="element-180" data-mp="2bbzv0zdk0t48">
                      6
                    </span>
                    Fotodokumentation
                  </h3>

                  <div className="space-y-4" data-id="element-181" data-mp="3k9g91dsqe91t">
                    {[{
                  label: 'Målerskab/Målerfelt (uden dæksel)',
                  state: photoMeterCabinet,
                  setter: setPhotoMeterCabinet
                }, {
                  label: 'Ladeboks 3 meter fra opsætning',
                  state: photoChargingBox3m,
                  setter: setPhotoChargingBox3m
                }, {
                  label: 'Tavle uden dæksel',
                  state: photoPanelWithoutCover,
                  setter: setPhotoPanelWithoutCover
                }, {
                  label: 'Tavle med dæksel',
                  state: photoPanelWithCover,
                  setter: setPhotoPanelWithCover
                }, {
                  label: 'Kabelføring (valgfrit)',
                  state: photoCableRouting,
                  setter: setPhotoCableRouting
                }].map((photo, idx) => <div key={idx} data-id="element-182" data-mp="3jk1am18t6gi2" data-mpi={idx}>
                        <label className="block text-sm font-medium text-stone-700 mb-2" data-id="element-183" data-mp="3jizsjctdktqu">
                          Foto: {photo.label}
                        </label>
                        <div className="flex items-center gap-3" data-id="element-184" data-mp="39llipw1yj37i">
                          <label className="flex-1 flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-stone-300 rounded-lg hover:border-teal-500 transition-colors cursor-pointer bg-white" data-id="element-185" data-mp="39ny6ipuec7sh">
                            <CameraIcon className="w-5 h-5 text-stone-500" data-id="element-186" data-mp="1u54k64sczn79" />
                            <span className="text-sm text-stone-600" data-id="element-187" data-mp="1u77hzazr6kmj">
                              {photo.state ? 'Billede uploadet' : 'Vælg billede'}
                            </span>
                            <input type="file" accept="image/*" onChange={handlePhotoUpload(photo.setter)} className="hidden" data-id="element-188" data-mp="inicp08sblfa" />
                          </label>
                          {photo.state && <img src={photo.state} alt="Preview" className="w-16 h-16 object-cover rounded-lg border border-stone-200" data-id="element-189" data-mp="xehki91sq5xt" />}
                        </div>
                      </div>)}
                  </div>
                </div>

                {/* Additional Notes */}
                <div className="bg-stone-50 rounded-xl p-6 border border-stone-200" data-id="element-190" data-mp="2tq83i19usbgd">
                  <h3 className="text-lg font-semibold text-stone-900 mb-4 flex items-center gap-2" data-id="element-191" data-mp="2yhq2wkw8a3z8">
                    <span className="w-8 h-8 bg-teal-600 text-white rounded-full flex items-center justify-center text-sm font-bold" data-id="element-192" data-mp="2jsttajeiwiwi">
                      7
                    </span>
                    Yderligere noter
                  </h3>

                  <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Tilføj eventuelle yderligere noter eller kommentarer..." rows={4} className="w-full px-4 py-2.5 rounded-lg border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all resize-none" data-id="element-193" data-mp="3chsejpdy0161" />
                </div>

                <div className="flex gap-3 pt-2 sticky bottom-0 bg-white pb-4 border-t border-stone-200" data-id="element-194" data-mp="1w0ei3mt46wj9">
                  <Button type="button" variant="secondary" onClick={onClose} className="flex-1" data-id="element-195" data-mp="1jk5ag4u02zdn">
                    Annuller
                  </Button>
                  <Button type="submit" variant="primary" className="flex-1" data-id="element-196" data-mp="1tjmc8b64st4c">
                    {editingReport ? 'Gem ændringer' : 'Opret rapport'}
                  </Button>
                </div>
              </form>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}